// ===========================================================================
// ProCare.SG — HTML allowlist sanitiser
// ---------------------------------------------------------------------------
// Blog articles are stored as HTML, so they get parsed into an inert document
// and rebuilt from an allowlist before they are ever saved or rendered.
// Anything not on the list — scripts, event handlers, javascript: URLs, style
// attributes, iframes — is dropped rather than escaped, so a pasted snippet
// from Word or a compromised account cannot introduce script into a page.
// ===========================================================================

const ALLOWED_TAGS = new Set([
    "P", "BR", "HR",
    "STRONG", "B", "EM", "I", "U", "S", "SUP", "SUB",
    "H2", "H3", "H4",
    "UL", "OL", "LI",
    "A", "IMG",
    "BLOCKQUOTE", "FIGURE", "FIGCAPTION",
    "CODE", "PRE", "SPAN", "DIV",
]);

// Tags that carry no semantics of their own once styling is stripped.
const UNWRAP_TAGS = new Set(["SPAN", "DIV", "FONT"]);

const ALLOWED_ATTRS = {
    A: ["href", "title"],
    IMG: ["src", "alt", "width", "height"],
};

const SAFE_URL = /^(https?:\/\/|mailto:|tel:|\/|#)/i;

function safeUrl(value) {
    const url = String(value || "").trim();
    if (!url) return null;
    // Reject anything that could resolve to a script-executing scheme.
    if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !SAFE_URL.test(url)) return null;
    if (!SAFE_URL.test(url)) return null;
    return url;
}

function cleanNode(node, doc) {
    if (node.nodeType === Node.TEXT_NODE) {
        return doc.createTextNode(node.nodeValue);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const tag = node.tagName.toUpperCase();
    const children = Array.from(node.childNodes)
        .map((child) => cleanNode(child, doc))
        .filter(Boolean);

    if (!ALLOWED_TAGS.has(tag)) {
        // Keep the readable content of a disallowed wrapper, discard the wrapper.
        if (tag === "SCRIPT" || tag === "STYLE" || tag === "IFRAME" || tag === "OBJECT" || tag === "EMBED") {
            return null;
        }
        const fragment = doc.createDocumentFragment();
        children.forEach((child) => fragment.appendChild(child));
        return fragment;
    }

    if (UNWRAP_TAGS.has(tag)) {
        const fragment = doc.createDocumentFragment();
        children.forEach((child) => fragment.appendChild(child));
        return fragment;
    }

    const element = doc.createElement(tag.toLowerCase());
    for (const attribute of ALLOWED_ATTRS[tag] || []) {
        const raw = node.getAttribute(attribute);
        if (raw == null) continue;
        if (attribute === "href" || attribute === "src") {
            const url = safeUrl(raw);
            if (url) element.setAttribute(attribute, url);
        } else {
            element.setAttribute(attribute, String(raw).slice(0, 300));
        }
    }

    if (tag === "A" && element.getAttribute("href")?.startsWith("http")) {
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noopener noreferrer nofollow");
    }
    if (tag === "IMG") {
        if (!element.getAttribute("src")) return null;
        element.setAttribute("loading", "lazy");
        if (!element.getAttribute("alt")) element.setAttribute("alt", "");
    }

    children.forEach((child) => element.appendChild(child));
    return element;
}

// Elements that stand on their own at the top level of an article.
const BLOCK_TAGS = new Set([
    "P", "H2", "H3", "H4", "UL", "OL", "BLOCKQUOTE", "FIGURE", "PRE", "HR", "IMG",
]);

/**
 * contentEditable readily leaves loose text and inline tags directly under the
 * root. Wrapping those runs in <p> keeps the stored article valid and makes it
 * style predictably on the public page.
 */
function wrapLooseInlines(container, doc) {
    const children = Array.from(container.childNodes);
    let paragraph = null;

    for (const node of children) {
        const isBlock = node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName);
        const isBlank = node.nodeType === Node.TEXT_NODE && !node.nodeValue.trim();

        if (isBlock) {
            paragraph = null;
            continue;
        }
        if (isBlank) {
            container.removeChild(node);
            continue;
        }
        if (!paragraph) {
            paragraph = doc.createElement("p");
            container.insertBefore(paragraph, node);
        }
        paragraph.appendChild(node);
    }
}

/** Sanitise an HTML string, returning safe HTML. */
export function sanitizeHtml(html) {
    if (!html) return "";
    // DOMParser produces an inert document: nothing loads, nothing executes.
    const parsed = new DOMParser().parseFromString(String(html), "text/html");
    const output = document.implementation.createHTMLDocument("");
    const container = output.createElement("div");

    Array.from(parsed.body.childNodes).forEach((node) => {
        const cleaned = cleanNode(node, output);
        if (cleaned) container.appendChild(cleaned);
    });

    wrapLooseInlines(container, output);

    return container.innerHTML
        .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
        .trim();
}

/** Readable plain text — used for excerpts and reading-time estimates. */
export function htmlToText(html) {
    if (!html) return "";
    const parsed = new DOMParser().parseFromString(String(html), "text/html");
    return (parsed.body.textContent || "").replace(/\s+/g, " ").trim();
}
